import { movingEdges, snapEdge, type Box } from './fit';
import { isPeeking, releaseTextures } from './picture';
import './arrows';   // หัวลูกศร on lines, for every canvas
import './handles';
import './textFxDraw';  // เอฟเฟกต์ข้อความ: the look from textFx, the พื้นหลัง box, the side handles less the box  // ที่จับชิ้นงานเล็ก: inside = move, short edges hide side handles, ✥ grip under tiny pieces
import { isEdited } from './photo';
import { fabric } from 'fabric';
import { BRK, JOIN } from '../thai/segment';
import { isThaiText, type ThaiTextbox } from '../thai/ThaiTextbox';
import { mmToPx, SAFE_MM } from '../print/rules';
import { atFit, keepSide, twoFingerSwipe, twoFingerTap, WheelSwipe } from './gestures';
import type { VideoProject } from '../video/project';
import { noGuides, type PageGuides } from './pageFormat';
import { guideLabel, type RulerUnit } from './rulerMath';
import { drawSlotMarks } from './picSlot';

/**
 * `name` is the design's name; `sizeName` the size preset it was set to (for highlighting it).
 * `bleedMm` is the print bleed on every side, outside w × h (saved inside the design JSON, see history.ts).
 * `video` is set when the open work is a video project (src/video/project.ts); also saved inside the design JSON.
 */
export interface Doc { w: number; h: number; name: string; bg: string; sizeName?: string; bleedMm?: number; video?: VideoProject }

/** Extra properties stored with every object (history, autosave, clone).
 *  `vStart`/`vEnd`/`vFade`: when a text/picture shows on a video (src/video/project.ts checkOverlays).
 *  `photoEdit`: a picture's light/colour settings (src/editor/photo.ts); its Fabric filters are made from them.
 *  `photoShape`: a cropped picture's circle/rounded frame (src/editor/crop.ts); its clipPath is made from it.
 *  `bgOriginal`: the picture's file before ลบพื้นหลัง (src/ui/bgRemove.ts), for "คืนพื้นหลัง".
 *  `groupId`: objects with the same one are a group (จัดกลุ่ม, src/ui/groupSelect.ts) — each stays its own object.
 *  `arrowStart`/`arrowEnd`: หัวลูกศร on a line (src/editor/arrows.ts); `pen`: a stroke drawn with ปากกา/มาร์กเกอร์/ไฮไลต์
 *  (what the eraser may remove); `note`: a โน้ตแปะ, on screen only (src/editor/drawing.ts, pageFormat.ts withoutNotes).
 *  `fixOriginal`: the picture's file before แก้รูป (src/ui/fixView.ts), for "คืนต้นฉบับ".
 *  `shapeId`/`shapeKey`: a shape from องค์ประกอบ → รูปทรง (src/editor/shapeLib.ts) and this one's own key; `shapeLink`:
 *  the text typed in it (= its shape's shapeKey, src/ui/shapePanel.ts). An old app drops them: only the link is lost.
 *  `picSlot`: ช่องใส่รูป of a ready-made template (src/editor/picSlot.ts); an old app drops it: a plain pale box. */
export const EXTRA_PROPS = ['wrapMode', 'name', 'locked', 'selectable', 'evented', 'hasControls',
  'lockMovementX', 'lockMovementY', 'lockRotation', 'lockScalingX', 'lockScalingY', 'vStart', 'vEnd', 'vFade', 'photoEdit', 'photoShape', 'bgOriginal', 'groupId', 'sub',
  'arrowStart', 'arrowEnd', 'pen', 'note', 'fixOriginal', 'gfx', 'textFx',
  'shapeId', 'shapeKey', 'shapeLink', 'picSlot',
  // ตาราง (src/table/tableModel.ts): the data a table group is drawn from
  'table',
  // เอกสาร (src/docs/docFields.ts, 0028): ช่องข้อมูล and a text's words in both languages
  'field', 'i18n'];

const css = (n: string) => getComputedStyle(document.documentElement).getPropertyValue(n).trim();

type TextInternals = ThaiTextbox & {
  _textLines: string[][];
  __charBounds: Array<Array<{ left: number }>>;
  _getLineLeftOffset(i: number): number;
  getHeightOfLine(i: number): number;
};

/*
 * Fabric writes numbers in the saved JSON with 2 decimals by default: a scale of 1.9187 came back as 1.92, so a
 * picture sized exactly to the page (เต็มหน้า, snapped edges) was a few px off after undo, saving or reopening — a white
 * line at the edge of a page without bleed (BUG-25). 6 decimals keep positions and sizes exact to well under a pixel.
 */
fabric.Object.NUM_FRACTION_DIGITS = 6;
/*
 * แต่งภาพ: Fabric's GPU filters work on one square of fabric.textureSize (default 2048) — a bigger picture comes out
 * cut off. 4096 here (an iPhone 12 MP photo is 4032 px); pictures larger than that are made smaller before editing
 * (src/editor/photo.ts EDIT_MAX_SIDE). A device whose GPU cannot take 4096 gets Fabric's slower CPU filters instead.
 */
fabric.textureSize = 4096;

export function createEditor(canvasEl: HTMLCanvasElement, stage: HTMLElement, onView: () => void) {
  fabric.Object.prototype.set({
    // padding 0: the selection frame sits on the object's real edge (as in Canva); the touch area of the handles stays 48
    transparentCorners: false, cornerStyle: 'circle', cornerSize: 16, touchCornerSize: 48, padding: 0, borderScaleFactor: 2
  } as Partial<fabric.Object>);

  const canvas = new fabric.Canvas(canvasEl, { preserveObjectStacking: true, selectionLineWidth: 2, stopContextMenu: true });
  const doc: Doc = { w: 1080, h: 1080, name: 'งานไม่มีชื่อ', bg: '#ffffff', sizeName: 'โพสต์สี่เหลี่ยม' };

  const board = new fabric.Rect({
    left: 0, top: 0, width: doc.w, height: doc.h, fill: doc.bg, selectable: false, evented: false,
    excludeFromExport: true, hoverCursor: 'default',
    shadow: new fabric.Shadow({ color: 'rgba(0,0,0,0.18)', blur: 24, offsetY: 4 })
  });
  canvas.add(board);

  const userObjects = () => canvas.getObjects().filter(o => o !== board);
  const unit = () => Math.max(doc.w, doc.h) / 1080;
  /** bleed in design px; the artboard grows by this on every side, objects keep their positions */
  const bleedPx = () => mmToPx(doc.bleedMm ?? 0);
  let showGuides = true;
  /**
   * เส้นไกด์ of the open page (design px from its top-left; history.ts owns them, src/ui/rulers.ts edits them) and the
   * one being dragged off a ruler (`drag`, with its position shown in `unit`). Drawn on the screen only, like the print
   * guides above — exports draw their own canvas.
   */
  let pageGuides: PageGuides = noGuides();
  let dragGuide: { axis: 'x' | 'y'; v: number; unit: RulerUnit; remove: boolean } | null = null;
  /** the "แต่งแล้ว" mark on edited pictures (screen only, never exported) — เพิ่มเติม can turn it off */
  let showEdited = true;

  /* theme-dependent colours live in CSS tokens */
  function paint() {
    const a = css('--accent');
    canvas.backgroundColor = css('--paper');
    const handles = { cornerColor: a, borderColor: a, cornerStrokeColor: a } as Partial<fabric.Object>;
    fabric.Object.prototype.set(handles);
    userObjects().forEach(o => o.set(handles));
    canvas.requestRenderAll();
  }
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', paint);
  new MutationObserver(paint).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

  /* view; `insetLeft` is the width covered by a side panel (tablet), kept clear of the artboard;
     the bottom holds the page strip or switcher and the size/zoom chips (styles.css .pstrip/.pagenav/.viewchips) —
     `insetBottom` is their height (setInsetBottom, src/ui/pageStrip.ts) */
  let insetLeft = 0;
  /** ไม้บรรทัด shown: this many css px along the top and the left are the rulers' (src/ui/rulers.ts) */
  let rulerPx = 0;
  let fitZoom = 0;              // zoom of "พอดีจอ" (a two-finger swipe turns the page only at this zoom)
  let insetBottom = 96;
  function fit() {
    const left = insetLeft + rulerPx;
    const W = canvas.getWidth() - left, H = canvas.getHeight() - insetBottom - rulerPx;
    const b = bleedPx(), fw = doc.w + 2 * b, fh = doc.h + 2 * b;
    const z = Math.min((W - 32) / fw, (H - 32) / fh);
    fitZoom = z;
    canvas.setViewportTransform([z, 0, 0, z, left + (W - fw * z) / 2 + b * z, rulerPx + (H - fh * z) / 2 + b * z]);
    onView();
  }
  /**
   * after a zoom or pan: a page smaller than the work area is centred, a bigger one keeps covering its middle
   * (src/editor/gestures.ts keepSide) — zooming never leaves the page off in a corner
   */
  function settleView() {
    const v = canvas.viewportTransform!, z = v[0], b = bleedPx();
    const W = canvas.getWidth(), H = canvas.getHeight() - insetBottom;
    const tx = keepSide(v[4], z, -b, doc.w + b, insetLeft + rulerPx, W);
    const ty = keepSide(v[5], z, -b, doc.h + b, rulerPx, H);
    if (tx !== v[4] || ty !== v[5]) canvas.setViewportTransform([z, 0, 0, z, tx, ty]);
  }
  /** the page strip opened or folded: fit the artboard into the space above it */
  function setInsetBottom(px: number) {
    if (px === insetBottom) return;
    insetBottom = px;
    fit();
  }
  /** the rulers shown (their thickness in css px) or hidden (0): fit the artboard into the space beside them */
  function setRulerSpace(px: number) {
    if (px === rulerPx) return;
    rulerPx = px;
    fit();
  }
  /** when a side panel opens or closes, fit the artboard into the space left beside it */
  function setInsetLeft(px: number) {
    if (px === insetLeft) return;
    insetLeft = px;
    fit();
  }
  let sized = false;
  new ResizeObserver(() => {
    canvas.setDimensions({ width: stage.clientWidth, height: stage.clientHeight });
    if (!sized) { sized = true; fit(); }
    canvas.requestRenderAll();
  }).observe(stage);

  function sizeBoard() {
    const b = bleedPx();
    board.set({ left: -b, top: -b, width: doc.w + 2 * b, height: doc.h + 2 * b }); board.setCoords();
  }
  function setDocSize(w: number, h: number) {
    doc.w = Math.round(w); doc.h = Math.round(h);
    sizeBoard(); fit(); canvas.requestRenderAll();
  }
  /** print bleed in mm (undefined = never set, same as 0) */
  function setBleed(mm: number | undefined) {
    if (doc.bleedMm === mm) return;
    doc.bleedMm = mm;
    sizeBoard(); fit(); canvas.requestRenderAll();
  }
  function setGuides(on: boolean) { showGuides = on; canvas.requestRenderAll(); }
  function setDocBg(c: string) { doc.bg = c; board.set('fill', c); canvas.requestRenderAll(); }

  /* touchpad/mouse wheel: up/down (and touchpad pinch) zooms; two fingers sideways turns the page */
  const wheelSwipe = new WheelSwipe();
  canvas.on('mouse:wheel', o => {
    const e = o.e as WheelEvent;
    if (WheelSwipe.sideways(e.deltaX, e.deltaY, e.ctrlKey)) {
      e.preventDefault(); e.stopPropagation();
      const dir = wheelSwipe.feed(e.deltaX, e.timeStamp);
      if (dir) onSwipe(dir);
      return;
    }
    const z = Math.min(8, Math.max(0.02, canvas.getZoom() * Math.pow(0.999, e.deltaY)));
    canvas.zoomToPoint(new fabric.Point(e.offsetX, e.offsetY), z); settleView(); onView();
    e.preventDefault(); e.stopPropagation();
  });

  /* two-finger pinch + pan; captured on the stage before Fabric sees the touches.
     A quick sideways two-finger stroke without pinching, started at "พอดีจอ", turns the page instead. */
  let pinch: { d: number; z: number; wx: number; wy: number; mx: number; my: number; t: number; fit: boolean; ex: number; ey: number; ed: number; v: number[] } | null = null;
  let onSwipe: (dir: -1 | 1) => void = () => {};
  let onTap2: (x: number, y: number) => void = () => {};
  const pt = (t: Touch) => { const r = canvasEl.parentElement!.getBoundingClientRect(); return { x: t.clientX - r.left, y: t.clientY - r.top }; };
  stage.addEventListener('touchstart', e => {
    if ((e.target as Element).closest('.sheet,.editbar,.toast,.selbar,.viewchips,.vview,.ocmenu,.pstrip,.pnbox,.drawrail,.drawfly')) return;   // .vview: the video screen has its own scrolling; .ocmenu: the object menu; .pstrip/.pnbox: the page strip, its menu and the page name box
    if (e.touches.length === 2) {
      const a = pt(e.touches[0]), b = pt(e.touches[1]), v = canvas.viewportTransform!;
      const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      pinch = { d, z: v[0], wx: (m.x - v[4]) / v[0], wy: (m.y - v[5]) / v[0], mx: m.x, my: m.y, t: e.timeStamp, fit: atFit(v[0], fitZoom), ex: m.x, ey: m.y, ed: d, v: [...v] };
      (canvas as unknown as { _currentTransform: unknown })._currentTransform = null;
      // drawing with ปากกา (src/ui/drawTools.ts): the first finger had started a stroke — two fingers are a pinch, so
      // that stroke is dropped (Fabric adds a stroke on mouse-up only while _isCurrentlyDrawing)
      const draw = canvas as unknown as { _isCurrentlyDrawing?: boolean; freeDrawingBrush?: { _points?: unknown[] }; contextTop: CanvasRenderingContext2D };
      if (canvas.isDrawingMode && draw._isCurrentlyDrawing) {
        draw._isCurrentlyDrawing = false;
        if (draw.freeDrawingBrush) draw.freeDrawingBrush._points = [];
        canvas.clearContext(draw.contextTop);
      }
      e.preventDefault(); e.stopPropagation();
    } else if (pinch) e.stopPropagation();
  }, { capture: true, passive: false });
  stage.addEventListener('touchmove', e => {
    if (!pinch) return;
    e.preventDefault(); e.stopPropagation();
    if (e.touches.length !== 2) return;
    const a = pt(e.touches[0]), b = pt(e.touches[1]), m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    pinch.ex = m.x; pinch.ey = m.y; pinch.ed = Math.hypot(a.x - b.x, a.y - b.y);
    const z = Math.min(8, Math.max(0.02, pinch.z * pinch.ed / pinch.d));
    canvas.setViewportTransform([z, 0, 0, z, m.x - pinch.wx * z, m.y - pinch.wy * z]); settleView(); onView();
  }, { capture: true, passive: false });
  /**
   * fingers lifted: a two-finger tap opens the menu at that point (the view goes back to exactly where it was);
   * a page swipe puts the page back to "พอดีจอ" and turns it; anything else stays a pinch/pan
   */
  function endTwoFingers(e: TouchEvent) {
    const p = pinch; pinch = null;
    if (!p) return;
    // the first finger reached Fabric as a press: on text that was already chosen, Fabric would start editing it when
    // the fingers lift (keyboard up, menu useless) — two fingers never mean "edit this text"
    canvas.getObjects().forEach(o => { (o as { __lastSelected?: boolean }).__lastSelected = false; });
    const stroke = { dx: p.ex - p.mx, dy: p.ey - p.my, dt: e.timeStamp - p.t, scale: p.ed / p.d };
    if (twoFingerTap(stroke)) {
      canvas.setViewportTransform(p.v); onView();
      onTap2(p.mx, p.my);
      return;
    }
    if (!p.fit) return;
    const dir = twoFingerSwipe(stroke);
    if (!dir) return;
    fit();
    onSwipe(dir);
  }
  // as before: the gesture lasts until every finger is up (a finger left behind does not start a selection box)
  stage.addEventListener('touchend', e => { if (pinch && e.touches.length === 0) endTwoFingers(e); }, { capture: true });
  stage.addEventListener('touchcancel', e => { if (e.touches.length === 0) pinch = null; }, { capture: true });

  /* snapping to artboard edges/centre and to other objects — while moving and while resizing */
  let guides: Array<{ x?: number; y?: number }> = [];
  /** the lines an object's edges and centre are pulled to */
  function snapLines(o: fabric.Object) {
    const bl = bleedPx();
    const xs = [0, doc.w / 2, doc.w], ys = [0, doc.h / 2, doc.h];
    if (bl) { xs.push(-bl, doc.w + bl); ys.push(-bl, doc.h + bl); }   // so a background can be pulled out to the bleed
    // objects snap to the page's guides (choice 4ก) — only while they are shown (hidden with the rulers, like Canva's
    // one "rulers and guides" switch): an invisible line pulling objects would look like a bug
    if (rulerPx) { xs.push(...pageGuides.x); ys.push(...pageGuides.y); }
    const sel = o.type === 'activeSelection' ? (o as fabric.ActiveSelection) : null;
    userObjects().forEach(x => {
      if (x === o || !x.visible || (sel && sel.contains(x))) return;
      const b = x.getBoundingRect(true, true);
      xs.push(b.left, b.left + b.width / 2, b.left + b.width); ys.push(b.top, b.top + b.height / 2, b.top + b.height);
    });
    return { xs, ys };
  }
  canvas.on('object:moving', e => {
    const o = e.target!; const th = 8 / canvas.getZoom(); const r = o.getBoundingRect(true, true);
    const { xs, ys } = snapLines(o);
    const near = (own: number[], cands: number[]) => {
      let best: { d: number; c: number } | null = null;
      for (const a of own) for (const c of cands) { const d = c - a; if (Math.abs(d) < th && (!best || Math.abs(d) < Math.abs(best.d))) best = { d, c }; }
      return best;
    };
    guides = [];
    const bx = near([r.left, r.left + r.width / 2, r.left + r.width], xs);
    const by = near([r.top, r.top + r.height / 2, r.top + r.height], ys);
    if (bx) { o.left = (o.left ?? 0) + bx.d; guides.push({ x: bx.c }); }
    if (by) { o.top = (o.top ?? 0) + by.d; guides.push({ y: by.c }); }
    o.setCoords();
  });
  // resizing by a handle: the moving edge is pulled onto a line; the opposite side stays where it is
  // a line is only its stroke thick: with the frame on its edge it would be a few px to tap on an iPad, so it keeps
  // a frame 14 px out (padding is not saved: set whenever one is added, opened or restored)
  canvas.on('object:added', e => { if (e.target?.type === 'line') e.target.set('padding', 14); });
  // แต่งภาพ: a picture with filters keeps a full-size GPU copy until it is freed — free it whenever a picture leaves
  // the canvas, including the clear() of every undo/redo/page change (BUG-28)
  canvas.on('object:removed', e => { if (e.target) releaseTextures(e.target); });

  interface ScaleTransform { corner?: string; originX?: string; originY?: string }
  /** the box of a turned-free object; null when it is turned (its box is not its edges) */
  const straightBox = (o: fabric.Object) => ((o.angle ?? 0) % 360) !== 0 ? null : o.getBoundingRect(true, true);
  // resizing by a handle: the moving edge is pulled onto a line; the fixed side stays where it is
  canvas.on('object:scaling', e => {
    const o = e.target!;
    const t = (e as unknown as { transform?: ScaleTransform }).transform;
    guides = [];
    const r0 = straightBox(o);
    if (!t?.corner || !r0) return;
    // which edge moves comes from where Fabric holds the object (correct after dragging past the other side;
    // resizing from the centre (Option/Alt) gives no moving edge, so no snapping)
    const { mx, my } = movingEdges(t.originX, t.originY, t.corner);
    const shift = !!(e.e as MouseEvent | undefined)?.[canvas.uniScaleKey as 'shiftKey'];
    const uniform = !!mx && !!my && (!!canvas.uniformScaling !== shift);
    // an outline kept at the same width (strokeUniform) does not grow with the scale
    const sw = o.strokeUniform ? (o.strokeWidth ?? 0) : 0;
    const s = snapEdge({ x: r0.left, y: r0.top, w: r0.width, h: r0.height }, mx, my, ...lines(o), 8 / canvas.getZoom(), uniform, { x: sw, y: sw });
    if (s.fx === 1 && s.fy === 1) return;
    o.set({ scaleX: (o.scaleX ?? 1) * s.fx, scaleY: (o.scaleY ?? 1) * s.fy });
    keepFixed(o, r0, mx, my);
    guides = s.guides;
  });
  // a text box's side handles change its width (not its scale): the same pull for the moving side
  canvas.on('object:resizing', e => {
    const o = e.target!;
    const t = (e as unknown as { transform?: ScaleTransform }).transform;
    guides = [];
    const r0 = straightBox(o);
    if (!t?.corner || !r0) return;
    const { mx } = movingEdges(t.originX, t.originY, t.corner);
    const s = snapEdge({ x: r0.left, y: r0.top, w: r0.width, h: r0.height }, mx, null, ...lines(o), 8 / canvas.getZoom(), false);
    if (s.fx === 1) return;
    o.set('width', (o.width ?? 0) * s.fx);
    if (isThaiText(o)) o.initDimensions();
    keepFixed(o, r0, mx, null);
    guides = s.guides;
  });
  const lines = (o: fabric.Object): [number[], number[]] => { const l = snapLines(o); return [l.xs, l.ys]; };
  /** after a size change, move the object so its fixed side is where it was */
  function keepFixed(o: fabric.Object, r0: { left: number; top: number; width: number; height: number }, mx: 'l' | 'r' | null, my: 't' | 'b' | null) {
    o.setCoords();
    const r1 = o.getBoundingRect(true, true);
    const dx = mx === 'l' ? (r0.left + r0.width) - (r1.left + r1.width) : mx === 'r' ? r0.left - r1.left : 0;
    const dy = my === 't' ? (r0.top + r0.height) - (r1.top + r1.height) : my === 'b' ? r0.top - r1.top : 0;
    o.set({ left: (o.left ?? 0) + dx, top: (o.top ?? 0) + dy });
    o.setCoords();
  }
  canvas.on('mouse:up', () => { if (guides.length) { guides = []; canvas.requestRenderAll(); } });

  /* overlays drawn after each frame: snap guides + visible ticks for manual break marks */
  canvas.on('after:render', (ev: unknown) => {
    const ctx = (ev as { ctx?: CanvasRenderingContext2D })?.ctx; if (!ctx) return;
    const v = canvas.viewportTransform!, a = css('--accent'), ok = css('--ok');
    ctx.save(); ctx.lineWidth = 1;
    const bl = bleedPx();
    if (bl && showGuides) {
      // print guides: bleed edge (red dashed), trim (cyan), safe area for text (grey dashed)
      const rect = (x: number, y: number, w: number, h: number) => ctx.strokeRect(x * v[0] + v[4], y * v[3] + v[5], w * v[0], h * v[3]);
      const safe = mmToPx(SAFE_MM);
      ctx.setLineDash([6, 4]); ctx.strokeStyle = '#E5484D'; rect(-bl, -bl, doc.w + 2 * bl, doc.h + 2 * bl);
      ctx.setLineDash([]); ctx.strokeStyle = '#00A3E0'; ctx.lineWidth = 1.5; rect(0, 0, doc.w, doc.h);
      ctx.setLineDash([4, 4]); ctx.strokeStyle = 'rgba(94,90,112,.85)'; ctx.lineWidth = 1; rect(safe, safe, doc.w - 2 * safe, doc.h - 2 * safe);
      ctx.setLineDash([]);
    }
    drawPageGuides(ctx, v);
    drawSlotMarks(ctx, v, userObjects());   // ช่องใส่รูป: a picture mark + its hint (screen only)
    for (const g of guides) {
      ctx.strokeStyle = a; ctx.beginPath();
      if (g.x !== undefined) { const x = g.x * v[0] + v[4]; ctx.moveTo(x, 0); ctx.lineTo(x, canvas.getHeight()); }
      else if (g.y !== undefined) { const y = g.y * v[3] + v[5]; ctx.moveTo(0, y); ctx.lineTo(canvas.getWidth(), y); }
      ctx.stroke();
    }
    if (showEdited) drawEditedMarks(ctx);
    const act = canvas.getActiveObject();
    if (isThaiText(act) && /[​⁠]/.test(act.text ?? '')) {
      const t = act as TextInternals;
      if (t.__charBounds) {
        const m = fabric.util.multiplyTransformMatrices(v, t.calcTransformMatrix());
        let top = -(t.height ?? 0) / 2;
        t._textLines.forEach((ln, i) => {
          const lh = t.getHeightOfLine(i), left = t._getLineLeftOffset(i), cap = lh / (t.lineHeight ?? 1);
          ln.forEach((gr, j) => {
            if (gr !== JOIN && gr !== BRK) return;
            const b = t.__charBounds[i]?.[j]; if (!b) return;
            const x = -(t.width ?? 0) / 2 + left + b.left;
            const p1 = fabric.util.transformPoint(new fabric.Point(x, top + cap * 0.05), m);
            const p2 = fabric.util.transformPoint(new fabric.Point(x, top + cap * 0.95), m);
            ctx.strokeStyle = gr === JOIN ? a : ok; ctx.lineWidth = 3;
            ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(p2.x, p2.y); ctx.stroke();
          });
          top += lh;
        });
      }
    }
    ctx.restore();
  });

  /**
   * a small "แต่งแล้ว" label at the top-left corner of every picture with a แต่งภาพ look (choice 1ก: always, so a
   * forgotten edit is seen without choosing the picture); "ต้นฉบับ" while ดูต้นฉบับ is held. Drawn over the canvas
   * after each frame — exports draw their own canvas, so it never reaches a file.
   */
  function drawEditedMarks(ctx: CanvasRenderingContext2D) {
    const pics = userObjects().filter(o => o.type === 'image' && o.visible !== false && isEdited((o as { photoEdit?: unknown }).photoEdit));
    if (!pics.length) return;
    const W = canvas.getWidth(), H = canvas.getHeight();
    // theme colours read once per frame (getComputedStyle is slow while dragging)
    const st = getComputedStyle(document.documentElement), v = (n: string) => st.getPropertyValue(n).trim();
    const fill = v('--primary') || '#6B3FD9', ink = v('--on-primary') || '#fff';
    ctx.font = `600 12px ${v('--ui') || 'sans-serif'}`;
    ctx.textBaseline = 'middle';
    for (const o of pics) {
      const r = o.getBoundingRect(false, true);   // screen px, also for a member of a multi-selection
      if (r.width < 40 || r.height < 24 || r.left > W || r.top > H || r.left + r.width < 0 || r.top + r.height < 0) continue;
      const peek = isPeeking(o), text = peek ? 'ต้นฉบับ' : 'แต่งแล้ว';
      const w = ctx.measureText(text).width + 14, x = Math.max(4, r.left + 6), y = Math.max(4, r.top + 6);
      ctx.fillStyle = peek ? 'rgba(20,20,30,.78)' : fill;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(x, y, w, 20, 10); else ctx.rect(x, y, w, 20);   // roundRect: Safari 16+
      ctx.fill();
      ctx.fillStyle = peek ? '#fff' : ink; ctx.fillText(text, x + 7, y + 10.5);
    }
  }
  function setEditedMarks(on: boolean) { showEdited = on; canvas.requestRenderAll(); }

  /* ─── เส้นไกด์ (src/ui/rulers.ts) ─── */
  const GUIDE_COLOUR = '#E5338A', GUIDE_REMOVE = 'rgba(229,51,138,.35)';
  function drawPageGuides(ctx: CanvasRenderingContext2D, v: number[]) {
    if (!rulerPx && !pageGuides.x.length && !pageGuides.y.length && !dragGuide) return;
    const W = canvas.getWidth(), H = canvas.getHeight();
    ctx.save();
    ctx.lineWidth = 1; ctx.setLineDash([]);
    const line = (axis: 'x' | 'y', at: number) => {
      ctx.beginPath();
      if (axis === 'x') { const x = Math.round(at * v[0] + v[4]) + 0.5; ctx.moveTo(x, 0); ctx.lineTo(x, H); }
      else { const y = Math.round(at * v[3] + v[5]) + 0.5; ctx.moveTo(0, y); ctx.lineTo(W, y); }
      ctx.stroke();
    };
    // with the rulers hidden the saved guides are hidden too (they belong to the rulers); they come back with them
    if (rulerPx) {
      ctx.strokeStyle = GUIDE_COLOUR;
      pageGuides.x.forEach(g => line('x', g));
      pageGuides.y.forEach(g => line('y', g));
    }
    if (dragGuide) {
      const d = dragGuide;
      ctx.strokeStyle = d.remove ? GUIDE_REMOVE : GUIDE_COLOUR;
      ctx.setLineDash(d.remove ? [4, 4] : []);
      line(d.axis, d.v);
      // the position, next to the line
      const text = d.remove ? 'ปล่อยเพื่อลบ' : guideLabel(d.v, d.unit);
      ctx.font = '600 12px sans-serif'; ctx.textBaseline = 'middle';
      const w = ctx.measureText(text).width + 12;
      const x = d.axis === 'x' ? d.v * v[0] + v[4] + 6 : rulerPx + 8;
      const y = d.axis === 'y' ? d.v * v[3] + v[5] - 16 : rulerPx + 8;
      ctx.setLineDash([]);
      ctx.fillStyle = GUIDE_COLOUR;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(x, y, w, 20, 6); else ctx.rect(x, y, w, 20);
      ctx.fill();
      ctx.fillStyle = '#fff'; ctx.fillText(text, x + 6, y + 10.5);
    }
    ctx.restore();
  }
  /** the open page's guides (set by history.ts whenever a page is shown or its guides change) */
  function setPageGuides(g: PageGuides) { pageGuides = { x: g.x.slice(), y: g.y.slice() }; canvas.requestRenderAll(); }
  /** the guide being dragged off a ruler, or null when it is let go */
  function setDragGuide(d: typeof dragGuide) { dragGuide = d; canvas.requestRenderAll(); }
  /** the lines a dragged guide snaps to: the page's edges and middle, and every object's edges and middle (design px) */
  function guideSnapLines(axis: 'x' | 'y'): number[] {
    const out = axis === 'x' ? [0, doc.w / 2, doc.w] : [0, doc.h / 2, doc.h];
    userObjects().forEach(o => {
      if (!o.visible) return;
      const b = o.getBoundingRect(true, true);
      if (axis === 'x') out.push(b.left, b.left + b.width / 2, b.left + b.width);
      else out.push(b.top, b.top + b.height / 2, b.top + b.height);
    });
    return out;
  }

  /** the page in design px; with `bleed`, out to the bleed line (a picture that fills the page reaches it for print) */
  function pageBox(bleed: boolean): Box {
    const b = bleed ? bleedPx() : 0;
    return { x: -b, y: -b, w: doc.w + 2 * b, h: doc.h + 2 * b };
  }

  /** add an object centred on the artboard and select it */
  function place(o: fabric.Object) {
    o.set({ left: (doc.w - o.getScaledWidth()) / 2, top: (doc.h - o.getScaledHeight()) / 2 }); o.setCoords();
    canvas.add(o); canvas.setActiveObject(o); paint();
  }

  /** re-run wrapping on every Thai text (after the no-break list changes) */
  function rewrapAll() {
    userObjects().forEach(o => { if (isThaiText(o)) { o.initDimensions(); o.setCoords(); } });
    canvas.requestRenderAll();
  }

  /** what a two-finger page swipe does (set by main.ts: open the previous/next page) */
  const setOnSwipe = (fn: (dir: -1 | 1) => void) => { onSwipe = fn; };
  /** what a two-finger tap does (x/y: CSS px over the canvas) — the object menu (src/ui/contextMenu.ts) */
  const setOnTwoFingerTap = (fn: (x: number, y: number) => void) => { onTap2 = fn; };

  return {
    canvas, board, doc, userObjects, unit, paint, fit, setInsetLeft, setInsetBottom, setDocSize, setDocBg, setBleed, setGuides, place, pageBox, rewrapAll, setOnSwipe, setOnTwoFingerTap, setEditedMarks,
    /* ไม้บรรทัด + เส้นไกด์ (src/ui/rulers.ts) */
    setRulerSpace, setPageGuides, setDragGuide, guideSnapLines,
    /** where the side panel ends (css px from the stage's left): the left ruler sits there */
    insetLeftPx: () => insetLeft,
    insetBottomPx: () => insetBottom
  };
}

export type Editor = ReturnType<typeof createEditor>;
